import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { Purchases, CustomerInfo } from '@revenuecat/purchases-js';
import { useGetIdentity } from 'react-admin';
import { MembershipType } from './paywall.model';

interface RevenueCatContextValue {
  purchases: Purchases | null;
  customerInfo: CustomerInfo | null;
  isLoading: boolean;
  refreshCustomerInfo: () => Promise<void>;
}

const RevenueCatContext = createContext<RevenueCatContextValue | undefined>(undefined);

export const RevenueCatProvider = ({ children }: { children: ReactNode }) => {
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [purchases, setPurchases] = useState<Purchases | null>(null);

  const { identity, isPending: identityLoading } = useGetIdentity();

  const refreshCustomerInfo = async () => {
    setIsLoading(true);
    try {
      const info = await Purchases.getSharedInstance().getCustomerInfo();
      setCustomerInfo(info);
    } catch (e) {
      setCustomerInfo(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (identityLoading) return;
    const appUserId = String(identity?.id ?? Purchases.generateRevenueCatAnonymousAppUserId());
    // if (!identity || !identity.id) return;
    // const appUserId = String(identity.id);
    // console.log('[RevenueCatProvider] User identity', identity);
    // console.log('[RevenueCatProvider] Configuring Purchases with appUserId:', appUserId);
    setPurchases(Purchases.configure({
      apiKey: import.meta.env.VITE_RC_WEB_BILLING_PUBLIC_API_KEY,
      appUserId: appUserId
    }));
    refreshCustomerInfo();
  }, [identity]);

  return (
    <RevenueCatContext.Provider value={{ customerInfo, isLoading, refreshCustomerInfo, purchases }}>
      {children}
    </RevenueCatContext.Provider>
  );
};

export const useRevenueCat = () => {
  const ctx = useContext(RevenueCatContext);
  if (!ctx) throw new Error('useRevenueCat must be used within RevenueCatProvider');
  return ctx;
};

export const useHasAccess = (membership: MembershipType): [boolean] => {

  if (membership === 'Free') return [true];

  const { isLoading, customerInfo } = useRevenueCat();
  const hasAccess = customerInfo?.entitlements.active[membership]?.isActive || false;
  return [hasAccess && !isLoading];
};